import {Exclude, Expose, instanceToPlain, plainToClass, Type} from "class-transformer";
import * as t from "io-ts";
import {Entity} from "./entity";
import {CompanyEntity} from "./company";
import {validateJSON, validateJSONNow} from "../web/validation";

/** Validator for a route type. */
export const RouteValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    companyId: t.string,
    regionId: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    companyName: t.string,
    routeGroupId: t.string,
    routeGroupName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number
  })
]);

/** Validator for an array of route types. */
export const RouteArrayValidator = t.array(RouteValidator);

export type Route = t.TypeOf<typeof RouteValidator>;
export type RouteArray = t.TypeOf<typeof RouteArrayValidator>;

/**
 * A route.
 */
@Exclude()
export class RouteEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  companyId!: string;

  @Expose()
  @Type(() => CompanyEntity)
  company?: CompanyEntity;

  @Expose()
  routeGroupId?: string;

  @Expose()
  routeGroupName?: string;

  @Expose()
  regionId!: string;

  @Expose()
  doNotUse = false;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = "ROUTE";

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for RouteEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(pipelineFlow: Route): RouteEntity {
    return plainToClass(RouteEntity, pipelineFlow, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Route> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RouteValidator, object);
  }

  asTypeValue(): Route {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RouteValidator, object);
  }

  get companyName(): string | undefined {
    return this.company?.name
  }
}
